/**
 * Display-only proper-casing for free-text fields extracted from resumes
 * (names, cities, job titles, tools, employers, education fields).
 *
 * Resume text arrives inconsistently cased — sometimes ALL CAPS, sometimes
 * lower case. We normalise ONLY those two cases. Any string that already
 * carries mixed case (e.g. "PowerPoint", "McKinsey & Company", "iSpring")
 * is assumed deliberate and returned untouched, so we never *create* new
 * ugliness out of values that were already correct.
 *
 * IMPORTANT: this is a render-time helper. It must never be used to rewrite
 * stored data — the LLM's raw extraction and the candidate's own portal
 * edits stay verbatim; only the on-screen presentation is normalised.
 */

// Tokens kept fully upper-cased when normalising (matched case-insensitively,
// punctuation stripped). Display-only — extend freely, no spec impact.
const ACRONYMS = new Set([
  'AWS', 'GCP', 'AZURE', 'SQL', 'NOSQL', 'API', 'APIS', 'SDK', 'HTML', 'CSS',
  'XML', 'JSON', 'CSV', 'PDF', 'UX', 'UI', 'LMS', 'LXP', 'LCMS', 'SCORM',
  'XAPI', 'AICC', 'CMS', 'CRM', 'ERP', 'HRIS', 'ATS', 'MOOC', 'KPI', 'KPIS',
  'ROI', 'SLA', 'SOP', 'QA', 'QC', 'IT', 'HR', 'PM', 'PMP', 'PMO', 'OKR',
  'MBA', 'BBA', 'BSC', 'MSC', 'BA', 'MA', 'BS', 'MS', 'PHD', 'BTECH', 'MTECH',
  'USA', 'UK', 'UAE', 'EU', 'US', 'NCR', 'AI', 'ML', 'NLP', 'AR', 'VR', 'XR',
]);

// Known mixed-case spellings keyed by their bare lower-case form. Best-effort
// for the common L&D / tech tools; extend as needed. Display-only.
const CANONICAL: Record<string, string> = {
  powerpoint: 'PowerPoint', sharepoint: 'SharePoint', onedrive: 'OneDrive',
  ispring: 'iSpring', xapi: 'xAPI', saas: 'SaaS', paas: 'PaaS', iaas: 'IaaS',
  javascript: 'JavaScript', typescript: 'TypeScript', github: 'GitHub',
  gitlab: 'GitLab', youtube: 'YouTube', linkedin: 'LinkedIn',
  wordpress: 'WordPress', mysql: 'MySQL', postgresql: 'PostgreSQL',
  mongodb: 'MongoDB', indesign: 'InDesign', mcafee: 'McAfee',
  storyline: 'Storyline', captivate: 'Captivate', camtasia: 'Camtasia',
  articulate: 'Articulate', vyond: 'Vyond', figma: 'Figma', canva: 'Canva',
};

// Connector words kept lower-case when not the first word of the string.
const SMALL_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'nor', 'but', 'for', 'to', 'of', 'in', 'on',
  'at', 'by', 'with', 'as', 'per', 'via', 'from', '&', 'de', 'la', 'le',
  'van', 'von', 'der', 'di', 'da',
]);

/** Capitalise a single word, with best-effort surname-particle handling. */
function capWord(w: string): string {
  if (!w) return w;
  const lower = w.toLowerCase();
  if (lower.startsWith('mc') && w.length > 2) {
    return 'Mc' + capWord(w.slice(2));
  }
  if (lower.startsWith("o'") && w.length > 2) {
    return "O'" + capWord(w.slice(2));
  }
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/** Format an atom (no '-' or '/' inside): canonical -> acronym -> small-word
 *  -> capitalised. Surrounding punctuation on the atom is preserved. */
function formatAtom(atom: string, isFirst: boolean): string {
  const bare = atom.replace(/[^A-Za-z0-9]/g, '');
  if (!bare) return atom; // pure punctuation, e.g. "&"
  // A single letter followed by a period is a middle initial ("A.") — keep it
  // upper-cased and never let it fall through to the small-word rule (which
  // would lowercase "A." to "a." in a name like "Jordan A. Reyes").
  if (/^[A-Za-z]\.$/.test(atom)) return atom.toUpperCase();
  const lower = bare.toLowerCase();
  if (CANONICAL[lower]) return atom.replace(bare, CANONICAL[lower]);
  if (ACRONYMS.has(bare.toUpperCase())) return atom.replace(bare, bare.toUpperCase());
  if (!isFirst && SMALL_WORDS.has(lower)) return lower;
  return capWord(atom);
}

/** Format one whitespace-delimited token, descending into '-' / '/' compounds
 *  so acronyms inside them (e.g. "UX/UI") are still recognised. */
function formatToken(tok: string, isFirst: boolean): string {
  const parts = tok.split(/([-/])/); // keep separators
  let firstAtom = isFirst;
  return parts
    .map((p) => {
      if (p === '-' || p === '/') return p;
      const out = formatAtom(p, firstAtom);
      firstAtom = false;
      return out;
    })
    .join('');
}

/** True when the string's letters are all upper OR all lower case — i.e. it
 *  carries no deliberate internal casing we should preserve. */
function isAllUpperOrLower(s: string): boolean {
  const letters = s.replace(/[^A-Za-z]/g, '');
  if (!letters) return false;
  return letters === letters.toUpperCase() || letters === letters.toLowerCase();
}

/**
 * Smart proper-case a single free-text value for display. Mixed-case input
 * is returned unchanged; ALL-CAPS / all-lowercase input is title-cased with
 * acronym, known-brand, small-word and surname-particle handling.
 */
export function formatProperNoun(value: string | null | undefined): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (!isAllUpperOrLower(s)) return s; // already deliberately cased
  return s
    .split(/\s+/)
    .map((t, i) => formatToken(t, i === 0))
    .join(' ');
}

/** Format each item of a string list (tools, industries, certifications…). */
export function formatProperNounList(
  values: string[] | null | undefined,
): string[] {
  if (!values) return [];
  return values.map((v) => formatProperNoun(v));
}

// ── Title Case for UI labels (headings, button text, modal titles) ──────────
//
// Distinct from formatProperNoun: this targets author-written label text, not
// resume data. Key differences:
//   - It NEVER up-cases a lower-case word into an acronym (so "Submit it" stays
//     "Submit It", not "Submit IT"). It only PRESERVES tokens the author
//     already typed in all-caps (e.g. "PDF", "JD", "XLSX", "2FA").
//   - Small connector words (of, to, from, and, …) are lower-cased in the
//     middle but always capitalised as the first or last word (AP title style).
//   - Hyphen/slash compounds ("Data-Rights", "UX/UI") are handled per part.
//   - Pure-symbol tokens (arrows, "·", "+") pass through untouched.

const TITLE_SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'if', 'in', 'into',
  'nor', 'of', 'off', 'on', 'onto', 'or', 'over', 'per', 'the', 'to', 'up',
  'via', 'vs', 'with',
]);

/** True when a bare token is an author-typed acronym we should leave as-is. */
function isAuthorAcronym(bare: string): boolean {
  return bare.length >= 2 && bare === bare.toUpperCase() && /[A-Z]/.test(bare);
}

/** Capitalise one label word, preserving all-caps acronyms and descending into
 *  hyphen/slash compounds. */
function capLabelWord(w: string): string {
  return w
    .split(/([-/])/)
    .map((part) => {
      if (part === '-' || part === '/') return part;
      const bare = part.replace(/[^A-Za-z0-9]/g, '');
      if (!bare) return part;
      if (isAuthorAcronym(bare)) return part;
      const lower = bare.toLowerCase();
      if (CANONICAL[lower]) return part.replace(bare, CANONICAL[lower]);
      return capWord(part);
    })
    .join('');
}

/**
 * Title-case an author-written UI label (heading / button / modal title).
 * Idempotent on already-title-cased strings. Leave enum/code values, emails,
 * and IDs to their own formatters — this is for human-readable label prose.
 */
export function titleCaseLabel(value: string | null | undefined): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  const tokens = s.split(/\s+/);
  const last = tokens.length - 1;
  return tokens
    .map((tok, i) => {
      if (!/[A-Za-z0-9]/.test(tok)) return tok; // arrows, "·", "+", "—"
      const bare = tok.replace(/[^A-Za-z0-9]/g, '');
      if (isAuthorAcronym(bare)) return tok; // preserve "PDF", "JD", "XLSX"
      const lower = bare.toLowerCase();
      if (i !== 0 && i !== last && TITLE_SMALL_WORDS.has(lower)) {
        return tok.toLowerCase();
      }
      return capLabelWord(tok);
    })
    .join(' ');
}
